import { ICompileService } from "angular";
import { RecordDataSource } from "../../../../records/grids";
import { ColDef } from "../../../../records/grids/columns/colDef";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../../records/grids/columns/filters/FilterProvider";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../../../records/grids/settings/gridSettingsService";
import { IQuery } from "../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { IRowCommandEventArgs } from "../../../../records/grids/controller";
import { Machine } from "../../models/machine";
import { OptionsFilter } from "../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import { machineConfigurationCellClass } from "../../components/machineConfigurationCellClass";
export interface IMultipleMoveFormModel {
  MachinesDetails: IMachineSoftwareUpgradeRequest[];
  Date?: Date;
  Files: File[];
  Notes?: string;
}

export interface IMachineSoftwareUpgradeRequest {
  MachineId: number;
  MachineBankId?: number;
  MachineConfigurationPosition?: string;
  SerialNumber: string;
  PropertyId?: number;
  PropertyName?: string;
  MachineAreaId?: number;
  MachineAreaName?: string;
  MachineZoneId?: number;
  MachineZoneName?: string;
  MachineBankName?: string;
  PreviousPropertyName?: string;
  PreviousMachineAreaName?: string;
  PreviousMachineZoneName?: string;
  PreviousMachineBankName?: string;
  PreviousPosition?: string;
}

const gridId = Symbol("main.machines.software");
export class MachinesSoftwareViewController {
  static $inject = [
    ServerUtilityName,
    GridSettingsServiceName,
    ColumnDefinitionServiceName,
    "$compile",
    "$state",
    "environment",
  ];

  readonly $state: any;
  readonly gridDataSource: RecordDataSource;
  readonly gridSettings: IGridSettings;
  readonly columns: ColDef[];
  readonly defaultQuery: IQuery;

  private gridSelection: any[] = [];

  currentOrFutureConfigurationsOnly: boolean = true;

  constructor(
    serverUtility: ServerUtility,
    gridSettingsService: GridSettingsService,
    columnDefinitionService: ColumnDefinitionService,
    $compile: ICompileService,
    $state: any,
    private readonly environment: { Date: Date }
  ) {
    this.defaultQuery = {
      CurrentConfigurationOrDateGreaterThanOrEqualTo: environment.Date,
    };

    this.$state = $state;
    this.gridDataSource = new RecordDataSource(
      serverUtility,
      "/api/machines/MachineConfigurationSoftware"
    );
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.columns = [
      columnDefinitionService.getSelectColDef({
        headerName: "Select",
      }),
      columnDefinitionService.getNumberColDef({
        field: "Id",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "SlotNumber",
        header: "Asset Number",
      }),
      columnDefinitionService.getStringColDef({
        field: "SerialNumber",
      }),
      columnDefinitionService.getStringColDef({
        field: "Status",
        filter: new FilterProvider("Statue", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              queryFieldNames: ["Status"],
              options: [
                {
                  display: "Decommissioned",
                  value: "Decommissioned",
                },
                {
                  display: "Floor",
                  value: "Floor",
                },
                {
                  display: "Warehoused",
                  value: "Warehoused",
                },
              ],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getDateColDef({
        field: "Date",
        header: "Project Date",
      }),
      columnDefinitionService.getStringColDef({
        field: "PrimaryTheme",
        header: "Primary Theme",
        filter: new FilterProvider("MachineConfigurationGameTheme", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "PropertyName",
        header: "Property",
      }),
      columnDefinitionService.getStringColDef({
        field: "Location",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgramNumber",
        header: "Program Number",
      }),
      columnDefinitionService.getStringColDef({
        field: "Software",
      }),
      columnDefinitionService.getStringColDef({
        field: "Version",
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineAreaName",
        header: "Area",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineZoneName",
        header: "Zone",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineBankName",
        header: "Bank",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "Position",
        header: "Position",
        hide: true,
      }),
      columnDefinitionService.getBooleanColDef({
        field: "CurrentConfiguration",
        header: "Current Configuration",
      }),
      columnDefinitionService.getDateColDef({
        field: "InServiceDate",
        header: "In Service Date",
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineModelName",
        header: "Model",
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineModelTypeName",
        header: "Model Type",
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineManufacturerName",
        header: "Manufacturer",
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineConfigurationOrder",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "Order",
        hide: true,
      }),
    ];

    this.columns.forEach(
      (c) =>
        (c.cellClass = machineConfigurationCellClass(
          environment.Date,
          "CurrentConfiguration",
          "Date"
        ))
    );
  }
  gridSelectionChanged(data: any, selected: boolean) {
    const id = data.Id;
    const existingSelectionIndex = this.gridSelection.indexOf(
      this.gridSelection.find((r) => r.Id === id)
    );
    if (!selected && existingSelectionIndex > -1) {
      this.gridSelection.splice(existingSelectionIndex, 1);
      return;
    }
    if (selected && existingSelectionIndex === -1) {
      this.gridSelection.push(data);
    }
  }
  async SoftwareUpgrade(args: IRowCommandEventArgs<Machine>): Promise<void> {
    this.$state.go(".SoftwareUpgrade", { softwares: this.gridSelection });
  }

  currentOrFutureConfigurationsOnlyChanged(
    currentConfigurationOrDateGreaterThanOrEqualTo: boolean
  ) {
    this.defaultQuery.CurrentConfigurationOrDateGreaterThanOrEqualTo =
      currentConfigurationOrDateGreaterThanOrEqualTo
        ? this.environment.Date
        : undefined;
  }
}
